import { useMedicineWorkspace } from "../../../../components/adminPanel/shared/MedicineWorkspace";
import React, { useId, useMemo, useRef, useState } from "react";
import { Badge, Box, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import MedicineRichTextEditor from "../../../../components/adminPanel/shared/MedicineRichTextEditor";
import { useDispatch } from "react-redux";
import { uploadTempPic } from "../../../../reduxToolkit/features/adminPanel/misc";
import { PF } from "../../../../config";
import { TranslationFieldButton } from "../../../../components/adminPanel/shared/TranslationControls";

const sectionKey = (section, index) => section._id ? String(section._id) : `${index}:${section.sectionName}`;
// Bind callbacks to the editor instance's original section, even during async teardown.
function SectionContentEditor({ identity, content, config, disabled, onReady, onChange }) {
  const identityRef = useRef(identity);
  const [ready, setReady] = useState(false);
  // CKEditor inserts UI siblings around its source element. Keep all of those nodes
  // inside a dedicated React-owned container so async teardown cannot leave ghost editors.
  return <Box data-section-editor={identity} data-editor-ready={ready} sx={{minWidth:0}}><MedicineRichTextEditor content={content || ""} config={config} disabled={disabled || !ready}
    onReady={editor => { onReady(identityRef.current, editor); setReady(true); }}
    onChange={(_, editor) => onChange(identityRef.current, editor)} /></Box>;
}

export default function AdditionalPage({ additionalSectionList = [], setAdditionalSectionList, translate1, onTranslate, disableTranslation, disabledEditor = false }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const editorRef = useRef(null);
  const panelId = `medicine-content-${useId().replace(/:/g, "")}`;
  const dispatch = useDispatch();
  const workspace = useMedicineWorkspace();
  const selectedIndex = Math.max(0, additionalSectionList.findIndex((section, index) => sectionKey(section, index) === selectedKey));
  const selected = additionalSectionList[selectedIndex];
  const activeKey = selected ? sectionKey(selected, selectedIndex) : null;
  const activeKeyRef = useRef(activeKey);
  activeKeyRef.current = activeKey;
  const title = section => translate1(section.sectionName) || section.sectionName;

  function updateContent(key, content) {
    setAdditionalSectionList(previous => {
      const index = previous.findIndex((section, position) => sectionKey(section, position) === key);
      if (index < 0 || (previous[index].content || "") === content) return previous;
      if (workspace?.formRef) workspace.formRef.current.dirty = true;
      return previous.map((section, position) => position === index ? { ...section, content } : section);
    });
  }
  function selectSection(_, nextKey) {
    if (nextKey === activeKey) return;
    const current = editorRef.current;
    if (current?.key === activeKey && current.editor.state === "ready") updateContent(activeKey, current.editor.getData());
    editorRef.current = null;
    setSelectedKey(nextKey);
  }
  function translateSelected(intent) {
    if (!selected || activeKeyRef.current !== activeKey) return;
    const current = editorRef.current;
    const content = current?.key === activeKey && current.editor.state === "ready" ? current.editor.getData() : selected.content || "";
    const sections = additionalSectionList.map((section, index) => sectionKey(section, index) === activeKey ? {...section, content} : section);
    updateContent(activeKey, content);
    // The saved request names the section explicitly; never infer it from a tab index.
    onTranslate({...intent, field:`section-name:${selected.sectionName}`}, {additionalSections:sections});
  }
  const editorConfig = useMemo(() => ({
    extraPlugins: [function tempImageUpload(editor) {
      editor.plugins.get("FileRepository").createUploadAdapter = loader => ({
        async upload() {
          const file = await loader.file;
          if (!file) throw new Error("Choose an image to upload.");
          const name = `${Date.now()}-${Math.random().toString(16).slice(2)}.${file.name.split(".").pop()}`;
          const data = new FormData();
          data.append("name", name);
          data.append("tempImg", file);
          const result = await dispatch(uploadTempPic(data)).unwrap();
          if (result?.failed) throw new Error(result.message || "Image upload failed.");
          return { default: `${PF}temp/${name}` };
        },
      });
    }],
  }), [dispatch]);

  if (!selected) return <Typography color="text.secondary">No additional sections yet.</Typography>;
  return <Box sx={{ minWidth: 0 }}>
    <Tabs value={activeKey} onChange={selectSection} variant="scrollable" scrollButtons="auto"
      allowScrollButtonsMobile selectionFollowsFocus aria-label="Medicine additional sections"
      sx={{ mb: 2, borderBottom: 1, borderColor: "divider", "& .MuiTab-root": { textTransform: "none", minHeight: 48 }, "& .Mui-selected": { bgcolor: "action.selected" } }}>
      {additionalSectionList.map((section, index) => <Tab key={sectionKey(section, index)} value={sectionKey(section, index)}
        id={`${panelId}-tab-${index}`} aria-controls={panelId} aria-label={title(section)}
        label={<Tooltip title={section.content ? "" : "No content yet"}>
          <Badge color="warning" variant="dot" invisible={!!section.content} sx={{ "& .MuiBadge-badge": { right: -6 } }}>
            <span>{title(section)}</span>
          </Badge>
        </Tooltip>} />)}
    </Tabs>
    <Box role="tabpanel" id={panelId} aria-labelledby={`${panelId}-tab-${selectedIndex}`} sx={{ minWidth: 0 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>{title(selected)}</Typography>
      {onTranslate && <TranslationFieldButton key={`translate-${activeKey}`} label={selected.sectionName}
        field={`section-name:${selected.sectionName}`}
        onSave={translateSelected} disabled={disableTranslation} />}
      <SectionContentEditor key={`editor-${activeKey}`} identity={activeKey} content={selected.content} config={editorConfig} disabled={disabledEditor}
        onReady={(key, editor) => { if (activeKeyRef.current === key) editorRef.current = { key, editor }; }}
        onChange={(key, editor) => {
          if (activeKeyRef.current === key && editorRef.current?.editor === editor) updateContent(key, editor.getData());
        }} />
    </Box>
  </Box>;
}
